
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import { trackEvent } from "../../utilities/analyticsService";
import pageRoutes from "../../data/pageRoutes";

const DOWNLOAD_EXTENSIONS = /\.(pdf|csv|xlsx?|docx?|pptx?|zip|png|jpe?g|stl|step|stp|obj|glb|gltf)(\?|#|$)/i;

const getElementLabel = (element) => {
	if (!element) return "";
	return String(
		element.getAttribute("data-track-label") ||
			element.getAttribute("aria-label") ||
			element.getAttribute("title") ||
			element.innerText ||
			element.value ||
			""
	)
		.replace(/\s+/g, " ")
		.trim()
		.slice(0, 120);
};

const getMachineIdFromPath = (path = "") => {
	const match = String(path || "").match(/^\/(?:machines|documentation|model-viewer|2d-model-viewer)\/([^/?#]+)/);
	return match?.[1] || "";
};

const getCleanPath = (path = "") => String(path || "").split(/[?#]/)[0] || "/";
const getRouteSegments = (path = "") =>
	getCleanPath(path)
		.split("/")
		.map((segment) => segment.trim())
		.filter(Boolean);

const PAGE_ROUTE_LABEL_OVERRIDES = {
	"/": "Home",
	"/home": "Home",
	"/login": "Login",
	"/logout": "Logout",
	"/register": "Register",
	"/forgot-password": "Forgot Password",
	"/reset-password": "Reset Password",
	"/verify-email": "Verify Email",
	"/accept-eula": "Accept EULA",
	"/machines": "My Machines",
	"/recommendations": "Recommendations",
	"/cart": "Cart",
	"/checkout": "Checkout",
	"/finder": "Part Finder",
	"/order-history": "Order History",
	"/track-order": "Track Order",
	"/settings": "Settings",
	"/profile": "Profile",
	"/admin": "Admin",
	"/rspl": "Recommended Spare Parts List",
	"/documentation": "Documentation",
	"/requests": "Customer Care Requests",
	"/support": "Support",
	"/saved_for_later": "Saved For Later",
	"/notifications": "Notifications",
	"/analytics": "Order Analytics",
	"/analytics/user": "User Analytics",
	"/services": "Services",
	"/service": "Service",
	"/service/digital-solutions": "Digital Solutions",
	"/service/digital-solutions/preventive-dashboard": "Preventive Dashboard",
	"/service/digital-solutions/predictive-dashboard": "Predictive Dashboard",
	"/service/digital-solutions/sustainable-oven-service-dashboard": "Sustainable Oven Service Dashboard",
	"/service/digital-solutions/machine-dashboard": "Machine Dashboard",
	"/service/cases": "Cases",
	"/service/workorders": "Work Orders",
	"/training": "Training",
	"/training/basic-training": "Basic Training",
	"/reports": "Reports",
	"/search-results": "Search Results",
	"/model-viewer": "3D Model Viewer",
	"/2d-model-viewer": "2D Model Viewer",
	"/public/model-check": "Public Model Check",
	"/help": "Help",
	"/parts": "Parts",
};
const PAGE_ROUTE_LABELS = {
	...pageRoutes.reduce((labels, page) => {
		if (page?.href && page?.name) labels[page.href] = page.name;
		return labels;
	}, {}),
	...PAGE_ROUTE_LABEL_OVERRIDES,
};

const formatUnknownPageName = (path = "") => {
	const [section = "Application"] = getRouteSegments(path);
	const label = section
		.replace(/[-_]+/g, " ")
		.replace(/\b\w/g, (value) => value.toUpperCase())
		.trim();
	return label ? `${label} Page` : "Application Page";
};

const getPageNameForPath = (path = "") => {
	const cleanPath = getCleanPath(path);
	const segments = getRouteSegments(cleanPath);

	if (PAGE_ROUTE_LABELS[cleanPath]) return PAGE_ROUTE_LABELS[cleanPath];
	if (segments[0] === "order-received") return "Order Placed";
	if (segments[0] === "order-error") return "Order Error";
	if (segments[0] === "order-failure") return "Order Failed";
	if (segments[0] === "machines" && segments.length >= 3) return "Part Details";
	if (segments[0] === "machines" && segments.length >= 2) return "Machine Details";
	if (segments[0] === "documentation" && segments.length >= 2) return "Machine Documents";
	if (segments[0] === "model-viewer") return "3D Model Viewer";
	if (segments[0] === "2d-model-viewer") return "2D Model Viewer";
	if (segments[0] === "order-history" && segments.length >= 2) return "Order History Details";
	if (segments[0] === "finder") return "Part Finder";
	if (segments[0] === "parts") return "Parts";
	return formatUnknownPageName(cleanPath);
};

const getPageMetadata = () => {
	const path = window.location.pathname || "/";
	const pageName = getPageNameForPath(path);

	return {
		path,
		page_path: path,
		page_name: pageName,
		page_title: pageName,
		search: window.location.search || "",
		hash: window.location.hash || "",
		url: window.location.href,
		title: document.title || pageName,
		referrer: document.referrer || "",
		machine_id: getMachineIdFromPath(path),
	};
};

const getUserAnalyticsMetadata = (user = {}) => {
	if (!user?.user_id) return {};
	return {
		profileId: user.user_id,
		user_id: user.user_id,
		user_name: user.user_display_name || user.user_name || "",
		user_email: user.user_email || "",
		customer_id: user.customer_id || "",
		customer_name: user.customer_name || "",
		site_id: user.site_id || "",
		site_name: user.site_name || "",
		legal_entity: user.legal_entity || user.site_legal_entity || "",
		role_id: user.role_id || user.role?.role_id || "",
		location: user.location || user.region || user.country || "",
		user_role: user.role?.role_name || user.user_role_name || user.user_role || "",
	};
};

const getRouteCategory = (path = "") => {
	if (path.startsWith("/model-viewer")) return "3d_model";
	if (path.startsWith("/2d-model-viewer")) return "2d_model";
	if (path.startsWith("/cart")) return "cart";
	if (path.startsWith("/checkout")) return "checkout";
	if (path.startsWith("/order-received")) return "checkout_success";
	if (path.startsWith("/order-error") || path.startsWith("/order-failure")) return "checkout_failure";
	if (path.startsWith("/admin")) return "admin";
	if (path.startsWith("/analytics")) return "analytics";
	if (path.startsWith("/search-results")) return "search";
	return "page";
};

const isPathOrChild = (path = "", root = "") => {
	const cleanPath = getCleanPath(path);
	return cleanPath === root || cleanPath.startsWith(`${root}/`);
};

const ORDER_FLOW_ENTRY_PATHS = [
	"/machines",
	"/search-results",
	"/recommendations",
	"/saved_for_later",
	"/requests",
	"/rspl",
	"/order-history",
	"/track-order",
	"/cart",
	"/checkout",
	"/order-error",
	"/order-failure",
	"/model-viewer",
	"/2d-model-viewer",
	"/notifications",
	"/finder",
	"/parts",
];

const ORDERABLE_ITEM_PATHS = [
	"/machines",
	"/search-results",
	"/recommendations",
	"/saved_for_later",
	"/requests",
	"/rspl",
	"/order-history",
	"/track-order",
	"/model-viewer",
	"/2d-model-viewer",
	"/notifications",
	"/finder",
	"/parts",
];

const CART_ACTION_LABEL_TOKENS = [
	"add to cart",
	"add all to cart",
	"add parts to cart",
	"add selected to cart",
	"add only purchasable",
	"add purchasable to cart",
	"no, just add to cart",
	"no just add to cart",
	"yes, add to cart",
	"yes add to cart",
	"yes, add both",
	"yes add both",
	"order again",
	"buy again",
	"move to cart",
	"order items",
];

const isMachineDetailPath = (path = "") => /^\/machines\/[^/]+\/?$/.test(getCleanPath(path));
const isPartDetailPath = (path = "") => /^\/machines\/[^/]+\/[^/]+\/?$/.test(getCleanPath(path));
const isOrderFlowEntryPath = (path = "") =>
	ORDER_FLOW_ENTRY_PATHS.some((root) => isPathOrChild(path, root));
const isOrderableItemPath = (path = "") =>
	ORDERABLE_ITEM_PATHS.some((root) => isPathOrChild(path, root)) || isMachineDetailPath(path) || isPartDetailPath(path);
const isCartActionLabel = (label = "") => {
	const normalizedLabel = String(label || "").toLowerCase();
	return CART_ACTION_LABEL_TOKENS.some((token) => normalizedLabel.includes(token));
};

const getFunnelStepForPath = (path = "") => {
	if (path.startsWith("/order-received")) return "order_completed";
	if (path.startsWith("/checkout") || path.startsWith("/order-error") || path.startsWith("/order-failure")) return "checkout";
	if (path.startsWith("/cart")) return "cart_ready";
	if (isOrderableItemPath(path)) return "viewed_order_items";
	if (isOrderFlowEntryPath(path)) return "order_entry";
	return "";
};

const trackOrderFunnelStep = (step, metadata = {}) => {
	if (!step) return;
	trackEvent(`funnel_${step}`, {
		...metadata,
		funnel_name: "order_conversion",
		funnel_step: step,
		step,
	});
};

const isDownloadTarget = (element) => {
	const href = element?.getAttribute?.("href") || "";
	return Boolean(element?.hasAttribute?.("download") || DOWNLOAD_EXTENSIONS.test(href));
};

const getInputDescriptor = (element) =>
	String(
		element?.getAttribute?.("data-track-source") ||
			element?.getAttribute?.("name") ||
			element?.getAttribute?.("id") ||
			element?.getAttribute?.("placeholder") ||
			element?.getAttribute?.("aria-label") ||
			""
	)
		.trim()
		.slice(0, 80);

const isSearchInput = (element) => {
	const type = String(element?.getAttribute?.("type") || "").toLowerCase();
	const descriptor = getInputDescriptor(element).toLowerCase();
	return type === "search" || descriptor.includes("search");
};

const isFilterControl = (element) => {
	const descriptor = getInputDescriptor(element).toLowerCase();
	return ["filter", "sort", "category", "status", "range", "customer", "site"].some((token) =>
		descriptor.includes(token)
	);
};

const OpenPanelPageTracker = () => {
	const location = useLocation();
	const { user } = useAuth();
	const userRef = useRef(user);
	useEffect(() => {
		userRef.current = user;
	}, [user]);

	useEffect(() => {
		const metadata = {
			...getPageMetadata(),
			...getUserAnalyticsMetadata(userRef.current),
		};
		const routeCategory = getRouteCategory(metadata.path);

		trackEvent("screen_view", {
			...metadata,
			route_category: routeCategory,
		});
		trackOrderFunnelStep(getFunnelStepForPath(metadata.path), metadata);

		if (["cart", "checkout", "checkout_success", "checkout_failure"].includes(routeCategory)) {
			trackEvent("checkout_funnel_step", {
				...metadata,
				step: routeCategory,
			});
		}
		if (routeCategory === "admin") {
			trackEvent("admin_page_viewed", metadata);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname, location.search, location.hash]);

	useEffect(() => {
		const searchTimers = new WeakMap();

		const handleClick = (event) => {
			const target = event.target?.closest?.("a,button,[role='button'],input[type='button'],input[type='submit']");
			if (!target) return;

			const metadata = {
				...getPageMetadata(),
				...getUserAnalyticsMetadata(user),
			};
			const payload = {
				...metadata,
				element_tag: target.tagName?.toLowerCase?.() || "",
				element_label: getElementLabel(target),
				element_role: target.getAttribute?.("role") || "",
				href: target.getAttribute?.("href") || "",
				action_category: getRouteCategory(metadata.path),
			};

			trackEvent("user_click", payload);

			if (isDownloadTarget(target)) {
				trackEvent("file_downloaded", payload);
			}

			if (isCartActionLabel(payload.element_label)) {
				trackEvent("cart_add_clicked", payload);
				trackOrderFunnelStep("cart_ready", payload);
			}

			if (metadata.path.startsWith("/admin")) {
				trackEvent("admin_action_clicked", payload);
			}
		};

		const handleSubmit = (event) => {
			const form = event.target;
			const metadata = {
				...getPageMetadata(),
				...getUserAnalyticsMetadata(user),
			};
			const formLabel = getElementLabel(form) || form.getAttribute?.("name") || form.getAttribute?.("id") || "";
			const searchField = form.querySelector?.("input[type='search'],input[name*='search' i],input[placeholder*='search' i]");
			if (searchField) {
				trackEvent("search_performed", {
					...metadata,
					search_source: getInputDescriptor(searchField) || formLabel || "form",
					search_term: String(searchField.value || "").trim().slice(0, 100),
				});
				return;
			}

			trackEvent("form_submitted", {
				...metadata,
				form_label: String(formLabel || "form").trim().slice(0, 100),
			});
		};

		const handleInput = (event) => {
			const target = event.target;
			if (!target || !isSearchInput(target)) return;
			const value = String(target.value || "").trim();
			if (value.length < 2) return;

			clearTimeout(searchTimers.get(target));
			const timer = setTimeout(() => {
				trackEvent("search_performed", {
					...getPageMetadata(),
					...getUserAnalyticsMetadata(user),
					search_source: getInputDescriptor(target) || "search",
					search_term: value.slice(0, 100),
					search_length: value.length,
				});
			}, 1000);
			searchTimers.set(target, timer);
		};

		const handleChange = (event) => {
			const target = event.target;
			const tagName = target?.tagName?.toLowerCase?.();
			const type = String(target?.getAttribute?.("type") || "").toLowerCase();
			if (!target || !["select", "input"].includes(tagName)) return;
			if (tagName === "input" && !["checkbox", "radio"].includes(type)) return;
			if (!isFilterControl(target)) return;

			trackEvent("filter_applied", {
				...getPageMetadata(),
				...getUserAnalyticsMetadata(user),
				filter_source: getInputDescriptor(target) || "filter",
				filter_value: String(target.value || "").slice(0, 100),
				filter_checked: typeof target.checked === "boolean" ? target.checked : undefined,
			});
		};

		document.addEventListener("click", handleClick, true);
		document.addEventListener("submit", handleSubmit, true);
		document.addEventListener("input", handleInput, true);
		document.addEventListener("change", handleChange, true);

		return () => {
			document.removeEventListener("click", handleClick, true);
			document.removeEventListener("submit", handleSubmit, true);
			document.removeEventListener("input", handleInput, true);
			document.removeEventListener("change", handleChange, true);
		};
	}, [user]);

	return null;
};

export default OpenPanelPageTracker;
